import {ErrorMessage, Form, Formik} from 'formik'
import {isEmpty} from 'lodash'
import {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {toast} from 'react-toastify'
import Modal from 'rsuite/Modal'
import {StateParamsModel} from 'src/app/modules/common/Model'
import checkMultiLanguage from 'src/app/modules/common/checkMultiLanguage'
import FormDatepicker from 'src/cms/helpers/components/forms/FormDatepicker'
import FormInputMediaManager from 'src/cms/helpers/components/forms/FormInputMediaManager'
import FormRadio from 'src/cms/helpers/components/forms/FormRadio'
import FormTextBox from 'src/cms/helpers/components/forms/FormTextBox'
import FormTinyMce from 'src/cms/helpers/components/forms/FormTinyMce'
import {imageBaseUrl} from 'src/cms/helpers/constants'
import * as Yup from 'yup'
import {safeText} from 'src/cms/helpers/validation/safeText'
import * as history from '../index'

type Props = {
  open: boolean
  params?: StateParamsModel
  handleClose: () => void
  actionType: string
  editSelectedData?: any
}

const FORM_VALIDATION = Yup.object().shape({
  fiscal_year: Yup.string().required('Fiscal Year is required'),
  title: safeText().nullable(),
  description: Yup.string().required('Description is required'),
  status: Yup.string().required('Status is required'),
})

const AddHistory = ({open, params, handleClose, actionType, editSelectedData}: Props) => {
  const dispatch = useDispatch()
  const isSingleLanguage = checkMultiLanguage()
  const [imagePath, setImagePath] = useState<any>('')
  const {loading, success} = useSelector((state: any) => state.history)

  const statusOptions = [
    {label: 'Active', value: 'Active'},
    {label: 'Inactive', value: 'Inactive'},
  ]

  useEffect(() => {
    if (success) {
      dispatch(history.actions.getHistory(params))
      isEmpty(editSelectedData)
        ? toast.success('History added successfully')
        : toast.success('History edited successfully')
      dispatch(history.actions?.addHistoryReset())
      handleClose()
    }
  }, [success])

  return (
    <div className='modal-container'>
      <Modal
        open={open}
        onClose={handleClose}
        className='w-75'
        backdrop='static'
        keyboard={false}
        enforceFocus={false}
        data-cy='modal'
      >
        <Modal.Header>
          <Modal.Title data-cy='modal-title'>{actionType} History</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            <Formik
              initialValues={{
                fiscal_year: '',
                title: '',
                description: '',
                description_np: '',
                image: '',
                status: 'Active',
              }}
              validationSchema={FORM_VALIDATION}
              onSubmit={(values, {}) => {
                const formData = {
                  ...values,
                  status: values?.status === 'Active' ? true : false,
                }

                if (!isEmpty(editSelectedData)) {
                  dispatch(history.actions.updateHistory(formData, editSelectedData?.id))
                } else {
                  dispatch(history.actions.addHistory(formData))
                }
              }}
            >
              {({touched, handleChange, errors, values, setFieldValue}) => {
                useEffect(() => {
                  if (!isEmpty(editSelectedData)) {
                    const fields = ['fiscal_year', 'title', 'description', 'description_np', 'image', 'status']
                    fields.forEach((field) => setFieldValue(field, editSelectedData[field], false))

                    setFieldValue(
                      'status',
                      editSelectedData?.status === true ? 'Active' : 'Inactive',
                      false
                    )
                  }
                }, [])

                return (
                  <Form>
                    <div className='card-body border-top pt-5'>
                      <div className='row'>
                        <div className='col-md-12 col-xs-12'>
                          <FormDatepicker
                            label='Fiscal Year'
                            name='fiscal_year'
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            required
                            showYearPicker
                            dateFormat='yyyy'
                            minDate={new Date(1900, 0, 1)}
                            maxDate={new Date()}
                            isClearable
                            errors={errors}
                            touched={touched}
                            value={
                              values.fiscal_year
                                ? new Date(Number(values.fiscal_year), 0, 1)
                                : null
                            }
                            setFieldValue={(field: string, date: Date | null) => {
                              setFieldValue(field, date ? date.getFullYear().toString() : '')
                            }}
                          />
                        </div>

                        <div className='col-md-12 col-xs-12'>
                          <FormTextBox
                            type='text'
                            labelClassName='col-md-12'
                            containerClassName='col-md-12'
                            placeholder='Enter Title'
                            label='Title (E.N)'
                            name='title'
                            onChange={handleChange}
                            errors={errors}
                            touched={touched}
                          />
                        </div>

                        <div className='col-md-6 col-xs-12'>
                          <FormTinyMce
                            containerClassName='col-md-12'
                            labelClassName='col-md-12'
                            label='Description (E.N)'
                            name='description'
                            handleChange={handleChange}
                            initialValue={
                              !isEmpty(editSelectedData) ? editSelectedData?.description : ''
                            }
                            required
                          />
                        </div>

                        {!isSingleLanguage && (
                          <div className='col-md-6 col-xs-12'>
                            <FormTinyMce
                              containerClassName='col-md-12'
                              labelClassName='col-md-12'
                              label='Description (N.P)'
                              name='description_np'
                              initialValue={
                                !isEmpty(editSelectedData) ? editSelectedData?.description_np : ''
                              }
                              handleChange={handleChange}
                            />
                          </div>
                        )}

                        <div className='col-md-6 col-xs-12'>
                          <FormInputMediaManager
                            label='Image'
                            name='image'
                            setFieldValue={setFieldValue}
                            setImageUrl={setImagePath}
                            value={values.image}
                          />

                          {!isEmpty(values.image) && (
                            <li className='listing'>
                              <div className='thumbImageBlock'>
                                <button
                                  type='button'
                                  className='btn thumbImage-remove btn-primary'
                                  onClick={() => {
                                    setImagePath('')
                                    setFieldValue('image', '')
                                  }}
                                >
                                  X
                                </button>

                                <img
                                  className='thumbImage w-100 h-100'
                                  src={`${imageBaseUrl}/${values.image}`}
                                  alt=''
                                />
                              </div>
                            </li>
                          )}
                        </div>

                        <div className='col-md-12 mt-5 col-xs-12'>
                          <label className='form-label fw-bolder text-dark fs-6 required'>
                            Status
                          </label>

                          <div className='d-flex ms-5'>
                            {statusOptions?.map((status: {label: string; value: string}) => (
                              <FormRadio
                                containerClassName=''
                                label='Select Status'
                                name='status'
                                type='radio'
                                onChange={handleChange}
                                errors={errors}
                                touched={touched}
                                required={true}
                                checkBoxText={status?.label}
                                value={status?.value}
                              />
                            ))}
                          </div>
                          <div className='fv-plugins-message-container text-danger fw-bolder small text-end mt-1'>
                            <ErrorMessage
                              name='status'
                              component='div'
                              className='field-error-message'
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className='d-flex justify-content-end px-9 '>
                      <button
                        type='submit'
                        disabled={loading}
                        className='btn btn-primary btn-sm ms-3'
                        data-cy='modal-submit'
                      >
                        Save
                      </button>
                      <button
                        type='button'
                        onClick={handleClose}
                        className='btn btn-secondary btn-sm ms-3'
                        data-cy='modal-cancel'
                      >
                        Cancel
                      </button>
                    </div>
                  </Form>
                )
              }}
            </Formik>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  )
}
export default AddHistory
